import type { PropsWithChildren } from "react";
import { createContext, useMemo } from "react";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type { CitationReference } from "../../../../../api/types/workflowTypes";
import { findFieldCitation, getFieldCitationKeys } from "./workflowDetailsHelpers";
import WorkflowRunDetailsContext from "./WorkflowRunDetailsContext";

interface ContextValue {
  citationFieldIds: Set<string>;
  activeCitationKey?: string;
  viewFieldCitation: (fieldName: string) => void;
}

const WorkflowFieldCitationContext = createContext<ContextValue | undefined>(undefined);

interface Props {
  activityId: string;
  citations?: CitationReference[];
}

export const WorkflowFieldCitationContextProvider = ({ activityId, citations, children }: PropsWithChildren<Props>) => {
  const { openFieldCitation, viewCitations } = useDefinedContext(
    WorkflowRunDetailsContext,
    "WorkflowRunDetailsContext"
  );

  const activeCitationKey = openFieldCitation?.activityId === activityId ? openFieldCitation.fieldId : undefined;

  const value = useMemo<ContextValue>(
    () => ({
      citationFieldIds: getFieldCitationKeys(citations),
      activeCitationKey,
      viewFieldCitation: (fieldName) => {
        const citation = findFieldCitation(citations, fieldName);
        if (citation) {
          viewCitations(activityId, citation.documentName, fieldName, citation.id);
        }
      },
    }),
    [activityId, citations, activeCitationKey, viewCitations]
  );

  return <WorkflowFieldCitationContext value={value}>{children}</WorkflowFieldCitationContext>;
};

export default WorkflowFieldCitationContext;
